import { useEffect, useState, type SyntheticEvent } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { startAuthentication, type PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { useAction, useMutation } from "convex/react";
import { Fingerprint, KeyRound, Mail } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";
import { authenticationOptionsForAuthenticator, detectPasskeyPlatform, passkeySignInLabel } from "./passkeyPlatform";
import { passwordSignInError } from "./signInError";

type Flow = "signIn" | "signUp" | "forgot" | "reset" | "resetSent";
type Checkout = { email: string; workspaceName: string; trial: boolean; trialEndsAt?: number; used: boolean };

// The yodl.cc edition: customers arrive from Stripe checkout and must accept
// the Terms of Service and Privacy Policy. John's private deployment keeps the
// invite-only wording and shows no legal links.
export const SELF_SERVE = import.meta.env.VITE_YODL_SELF_SERVE === "1";
export const SITE_URL = "https://yodl.cc";

function initialCheckout() {
  const sessionId = new URLSearchParams(window.location.search).get("checkout") ?? "";
  return /^cs_(live|test)_[A-Za-z0-9]{10,}$/.test(sessionId) ? sessionId : null;
}

export function LegalLinks() {
  if (!SELF_SERVE) return null;
  return <p className="auth-legal"><a href={`${SITE_URL}/terms/`} target="_blank" rel="noreferrer">Terms of Service</a> · <a href={`${SITE_URL}/privacy/`} target="_blank" rel="noreferrer">Privacy Policy</a> · <a href={SITE_URL} target="_blank" rel="noreferrer">yodl.cc</a></p>;
}

function initialReset() {
  const params = new URLSearchParams(window.location.search);
  const resetEmail = params.get("resetEmail") ?? "";
  const resetToken = params.get("resetToken") ?? "";
  return { resetEmail, resetToken, hasReset: resetEmail.length > 0 && resetToken.length > 0 };
}

export function AuthScreen() {
  const passkeyPlatform = detectPasskeyPlatform(navigator.userAgent);
  const windowsPasskey = passkeyPlatform === "windows";
  const [reset] = useState(initialReset);
  const [checkoutSessionId] = useState(initialCheckout);
  const { signIn } = useAuthActions();
  const beginPasskey = useAction(api.passkeys.beginAuthentication);
  const claimCheckout = useAction(api.billing.claimCheckout);
  const claimPasskeySession = useMutation(api.passkeyStore.claimAuthenticationGrant);
  const [flow, setFlow] = useState<Flow>(reset.hasReset ? "reset" : "signIn");
  const [email, setEmail] = useState(reset.resetEmail);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (reset.hasReset) window.history.replaceState({}, "", window.location.pathname); }, [reset.hasReset]);
  // Arriving from the yodl.cc welcome page: confirm the Stripe checkout, then
  // open the sign-up form on the buyer's email. The session id leaves the URL
  // right away so it is not kept in history or shared by accident.
  useEffect(() => {
    if (checkoutSessionId === null || !SELF_SERVE) return;
    window.history.replaceState({}, "", window.location.pathname);
    setBusy(true);
    claimCheckout({ sessionId: checkoutSessionId }).then((grant) => {
      setCheckout(grant);
      setEmail(grant.email);
      setFlow(grant.used ? "signIn" : "signUp");
    }).catch((caught: unknown) => { setError(errorMessage(caught)); }).finally(() => { setBusy(false); });
  }, [checkoutSessionId, claimCheckout]);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault(); setError(null); setBusy(true);
    try {
      if (flow === "forgot") {
        await signIn("password", { email: email.trim().toLowerCase(), flow: "reset" });
        setFlow("resetSent"); return;
      }
      if (flow === "reset") {
        if (password !== confirmPassword) throw new Error("The passwords do not match.");
        await signIn("password", { email: email.trim().toLowerCase(), flow: "reset-verification", code: reset.resetToken, newPassword: password });
        window.history.replaceState({}, "", window.location.pathname); return;
      }
      if (flow === "signUp" && SELF_SERVE && !termsAccepted) throw new Error("Please accept the Terms of Service and Privacy Policy.");
      await signIn("password", { email: email.trim().toLowerCase(), password, flow, ...(flow === "signUp" ? { name: name.trim(), ...(SELF_SERVE ? { termsAccepted: "1" } : {}) } : {}) });
    } catch (caught) {
      const message = errorMessage(caught);
      setError(flow === "signIn" ? passwordSignInError(caught, passkeyPlatform) : message.includes("InvalidSecret") || message.includes("Invalid password") ? "That email and password don't match." : message.includes("InvalidAccountId") || message.includes("Account not found") ? "No account with that email yet. Switch to “Create account”." : message);
    } finally { setBusy(false); }
  };

  const passkeySignIn = async () => {
    setBusy(true); setError(null);
    let flowId: string | null = null;
    try {
      if (!("PublicKeyCredential" in window)) throw new Error("Passkeys are not supported on this device.");
      const request = await beginPasskey({ authenticator: "any" });
      flowId = request.flowId;
      const options = authenticationOptionsForAuthenticator(JSON.parse(request.optionsJson) as PublicKeyCredentialRequestOptionsJSON, "any");
      const response = await startAuthentication({ optionsJSON: options });
      window.sessionStorage.setItem("yodl-passkey-flow", request.flowId);
      await signIn("passkey", { flowId: request.flowId, responseJson: JSON.stringify(response) });
      try {
        await claimPasskeySession({ flowId: request.flowId });
        window.sessionStorage.removeItem("yodl-passkey-flow");
      } catch {
        // The authenticated gate retries this one-use claim if the auth token
        // reaches React a moment after signIn resolves.
      }
    } catch (caught) {
      if (flowId !== null) window.sessionStorage.removeItem("yodl-passkey-flow");
      const message = errorMessage(caught);
      setError(message.includes("NotAllowedError")
        ? windowsPasskey
          ? "No passkey was returned. Try again and choose iPhone, iPad, or Android device — not Security key."
          : "Passkey sign-in was canceled or timed out."
        : "We couldn’t verify that passkey. A password alone cannot open Yodl.");
    } finally { setBusy(false); }
  };

  const title = flow === "signUp" ? (checkout !== null ? `Set up ${checkout.workspaceName || "your workspace"}` : "Create your account") : flow === "forgot" ? "Reset your password" : flow === "reset" ? "Choose a new password" : flow === "resetSent" ? "Check your email" : "Sign in";
  const trialEnds = checkout?.trialEndsAt !== undefined ? new Date(checkout.trialEndsAt).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : null;
  const signUpLede = checkout !== null
    ? `${checkout.trial ? `Your 14-day free trial is ready${trialEnds !== null ? ` and your card is not charged until ${trialEnds}` : ""}.` : "Your Yodl plan is ready."} Create the owner account for ${checkout.email}, then add a passkey.`
    : SELF_SERVE ? "Use the email you entered at checkout on yodl.cc, or the exact address that received your invitation." : "Use the exact email address that received your invitation.";
  const signInLede = checkout?.used === true ? `${checkout.email} already has a Yodl account. Sign in to open ${checkout.workspaceName || "your workspace"}.` : "Welcome back.";
  return (
    <main className="auth-screen" id="main-content">
      <form className="auth-card" onSubmit={(event) => { void submit(event); }}>
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div><h1>{title}</h1>
        <p className="auth-lede">{flow === "signUp" ? signUpLede : flow === "forgot" ? "We’ll email a private, one-use reset link that expires in 10 minutes." : flow === "reset" ? "Use at least 10 characters. Your other signed-in devices will be logged out." : flow === "resetSent" ? "If that address has a Yodl account, its secure reset link is on the way." : signInLede}</p>
        {flow === "resetSent" ? <><div className="auth-confirmation"><Mail size={20} /><span>Open the link in the email to continue.</span></div><Button type="button" variant="ghost" onClick={() => { setFlow("signIn"); }}>Back to sign in</Button></> : <>
          {flow === "signUp" ? <label>Your name<input name="name" value={name} onChange={(event) => { setName(event.target.value); }} autoComplete="name" placeholder="Alex Rivera" required /></label> : null}
          {flow !== "reset" ? <label>Email<input name="email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); }} autoComplete="email" spellCheck={false} placeholder="you@company.com" readOnly={flow === "signUp" && checkout !== null && !checkout.used} required /></label> : null}
          {flow !== "forgot" ? <label>{flow === "reset" ? "New password" : "Yodl password"}<input name="password" type="password" value={password} onChange={(event) => { setPassword(event.target.value); }} autoComplete={flow === "signIn" ? "current-password" : "new-password"} minLength={10} placeholder="At least 10 characters" required /></label> : null}
          {flow === "reset" ? <label>Confirm new password<input name="confirm-password" type="password" value={confirmPassword} onChange={(event) => { setConfirmPassword(event.target.value); }} autoComplete="new-password" minLength={10} required /></label> : null}
          {flow === "signUp" && SELF_SERVE ? <label className="check"><input name="accept-terms" type="checkbox" checked={termsAccepted} onChange={(event) => { setTermsAccepted(event.target.checked); }} required /><span>I agree to the <a href={`${SITE_URL}/terms/`} target="_blank" rel="noreferrer">Terms of Service</a> and <a href={`${SITE_URL}/privacy/`} target="_blank" rel="noreferrer">Privacy Policy</a>.</span></label> : null}
          {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
          <Button type="submit" variant="primary" disabled={busy}>{busy ? "One moment…" : flow === "signUp" ? "Create account" : flow === "forgot" ? "Email reset link" : flow === "reset" ? "Save new password" : "Sign in with Yodl password"}</Button>
          {flow === "signIn" && !windowsPasskey ? <Button type="button" variant="ghost" disabled={busy} onClick={() => { void passkeySignIn(); }}><Fingerprint size={17} /> {passkeySignInLabel(passkeyPlatform)}</Button> : null}
          {flow === "signIn" && windowsPasskey ? <p className="passkey-enrollment__note">Sign in with your Yodl password first. Then choose Windows Hello, phone QR, email code, or another passkey manager.</p> : null}
          {flow === "signIn" ? <button type="button" className="link-button" onClick={() => { setFlow("forgot"); setError(null); }}><KeyRound size={13} /> Forgot password?</button> : null}
          {flow !== "reset" ? <button type="button" className="link-button" onClick={() => { setFlow(flow === "signIn" ? "signUp" : "signIn"); setError(null); }}>{flow === "signIn" ? "New here? Create account" : "Back to sign in"}</button> : null}
          {flow === "signIn" && SELF_SERVE && checkout === null ? <a className="link-button" href={`${SITE_URL}/pricing/`}>No account yet? Start a free trial at yodl.cc</a> : null}
        </>}
        <LegalLinks />
      </form>
    </main>
  );
}
